import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const StudentFieldsManager = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getStudentFields();
      setFields(res.data);
    } catch (error) {
      toast.error('Failed to load fields');
    } finally {
      setLoading(false);
    }
  };

  const fieldSchema = Yup.object().shape({
    field_name: Yup.string().required('Field name required'),
    field_code: Yup.string()
      .required('Field code required')
      .matches(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, underscore'),
    display_name: Yup.string().required('Display name required'),
    field_type: Yup.string().required('Field type required'),
    is_compulsory: Yup.boolean(),
    is_unique: Yup.boolean(),
    options: Yup.string().test('options-json', 'Invalid JSON for options', function(value) {
      if (this.parent.field_type !== 'select') return true;
      if (!value) return true;
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }),
    validation_rules: Yup.string().test('valid-json', 'Invalid JSON', function(value) {
      if (!value) return true;
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }),
    display_order: Yup.number().integer().min(0).default(0),
    is_active: Yup.boolean(),
  });

  const handleSave = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        ...values,
        options: values.field_type === 'select' && values.options ? JSON.parse(values.options) : null,
        validation_rules: values.validation_rules ? JSON.parse(values.validation_rules) : null,
      };
      if (editingField) {
        await adminAPI.updateStudentField(editingField.id, payload);
        toast.success('Field updated');
      } else {
        await adminAPI.createStudentField(payload);
        toast.success('Field created');
      }
      setShowModal(false);
      resetForm();
      setEditingField(null);
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this field? This may affect existing student data.')) return;
    try {
      await adminAPI.deleteStudentField(id);
      toast.success('Field deleted');
      fetchFields();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const openCreateModal = () => {
    setEditingField(null);
    setShowModal(true);
  };

  const openEditModal = (field) => {
    setEditingField(field);
    setShowModal(true);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Student Registration Fields</h1>
          <p className="text-gray-600">Manage custom fields for student admission and profile</p>
        </div>
        <button onClick={openCreateModal} className="btn-primary flex items-center">
          <i className="fas fa-plus mr-2"></i> Add Field
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Fields</p>
              <p className="text-2xl font-bold">{fields.length}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-full"><i className="fas fa-list text-primary-600"></i></div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Fields</p>
              <p className="text-2xl font-bold">{fields.filter(f => f.is_active).length}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full"><i className="fas fa-check-circle text-green-600"></i></div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between">
            <div>
              <p className="text-gray-500 text-sm">Compulsory Fields</p>
              <p className="text-2xl font-bold">{fields.filter(f => f.is_compulsory).length}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full"><i className="fas fa-exclamation-triangle text-yellow-600"></i></div>
          </div>
        </div>
      </div>

      {/* Fields Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Custom Fields List</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Display Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Options</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fields.map(field => (
                <tr key={field.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium">{field.display_name}</div>
                    <div className="text-xs text-gray-500">{field.field_name}</div>
                   </td>
                  <td className="px-6 py-4 font-mono text-sm">{field.field_code}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{field.field_type}</span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {field.field_type === 'select' && field.options ? field.options.join(', ') : '-'}
                  </td>
                  <td className="px-6 py-4">
                    {field.is_compulsory ? <span className="text-red-600">Yes</span> : 'No'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${field.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {field.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 space-x-2">
                    <button onClick={() => openEditModal(field)} className="text-primary-600 hover:text-primary-900"><i className="fas fa-edit"></i></button>
                    <button onClick={() => handleDelete(field.id)} className="text-red-600 hover:text-red-900"><i className="fas fa-trash"></i></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingField ? 'Edit Field' : 'Create New Field'} size="lg">
        <Formik
          initialValues={{
            field_name: editingField?.field_name || '',
            field_code: editingField?.field_code || '',
            display_name: editingField?.display_name || '',
            field_type: editingField?.field_type || 'text',
            is_compulsory: editingField?.is_compulsory || false,
            is_unique: editingField?.is_unique || false,
            options: editingField?.options ? JSON.stringify(editingField.options) : '',
            validation_rules: editingField?.validation_rules ? JSON.stringify(editingField.validation_rules) : '{}',
            display_order: editingField?.display_order || 0,
            is_active: editingField?.is_active ?? true,
          }}
          validationSchema={fieldSchema}
          onSubmit={handleSave}
        >
          {({ isSubmitting, values, setFieldValue, errors, touched }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Field Name *</label>
                  <Field name="field_name" className="input-field" placeholder="e.g., passport_url" />
                  {errors.field_name && touched.field_name && <div className="text-red-500 text-sm">{errors.field_name}</div>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Field Code *</label>
                  <Field name="field_code" className="input-field font-mono" placeholder="e.g., passport_url" />
                  {errors.field_code && touched.field_code && <div className="text-red-500 text-sm">{errors.field_code}</div>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Display Name *</label>
                <Field name="display_name" className="input-field" placeholder="e.g., Passport Photo URL" />
                {errors.display_name && touched.display_name && <div className="text-red-500 text-sm">{errors.display_name}</div>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Field Type *</label>
                  <Field as="select" name="field_type" className="input-field">
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="select">Select</option>
                    <option value="boolean">Boolean (Yes/No)</option>
                    <option value="file">File Upload</option>
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Order</label>
                  <Field type="number" name="display_order" className="input-field" />
                </div>
              </div>
              {values.field_type === 'select' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Options (JSON array)</label>
                  <Field name="options" as="textarea" rows="2" className="input-field" placeholder='["Option 1", "Option 2"]' />
                  <p className="text-xs text-gray-500">Enter a valid JSON array of strings</p>
                </div>
              )}
              
              {/* Conditional Validation Rules Builder */}
              {values.field_type === 'file' ? (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">File Validation Rules</label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Allowed Extensions</label>
                      <div className="flex flex-wrap gap-3">
                        {['jpg', 'jpeg', 'png', 'pdf'].map(ext => {
                          let currentRules = {};
                          try {
                            currentRules = JSON.parse(values.validation_rules || '{}');
                          } catch { currentRules = {}; }
                          const allowed = currentRules.allowed_extensions || [];
                          const isChecked = allowed.includes(ext);
                          return (
                            <label key={ext} className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  let rules = {};
                                  try {
                                    rules = JSON.parse(values.validation_rules || '{}');
                                  } catch { rules = {}; }
                                  const exts = rules.allowed_extensions || [];
                                  if (e.target.checked) {
                                    if (!exts.includes(ext)) exts.push(ext);
                                  } else {
                                    const idx = exts.indexOf(ext);
                                    if (idx !== -1) exts.splice(idx, 1);
                                  }
                                  rules.allowed_extensions = exts;
                                  setFieldValue('validation_rules', JSON.stringify(rules, null, 2));
                                }}
                                className="mr-1"
                              />
                              <span className="text-sm">.{ext}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Select which file types are allowed.</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Max File Size (MB)</label>
                      <input
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={(() => {
                          try {
                            const rules = JSON.parse(values.validation_rules || '{}');
                            return rules.max_file_size_mb || 5;
                          } catch { return 5; }
                        })()}
                        onChange={(e) => {
                          let rules = {};
                          try {
                            rules = JSON.parse(values.validation_rules || '{}');
                          } catch { rules = {}; }
                          rules.max_file_size_mb = parseFloat(e.target.value) || 5;
                          setFieldValue('validation_rules', JSON.stringify(rules, null, 2));
                        }}
                        className="input-field w-32"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum allowed file size in megabytes.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Validation Rules (JSON, optional)</label>
                  <Field name="validation_rules" as="textarea" rows="2" className="input-field font-mono" placeholder='{"min": 0, "max": 100}' />
                  <p className="text-xs text-gray-500">Enter valid JSON. For file fields a visual builder is provided.</p>
                </div>
              )}

              <div className="flex space-x-6">
                <label className="flex items-center">
                  <Field type="checkbox" name="is_compulsory" className="mr-2" />
                  <span className="text-sm">Compulsory field</span>
                </label>
                <label className="flex items-center">
                  <Field type="checkbox" name="is_unique" className="mr-2" />
                  <span className="text-sm">Unique value (across students)</span>
                </label>
                <label className="flex items-center">
                  <Field type="checkbox" name="is_active" className="mr-2" />
                  <span className="text-sm">Active</span>
                </label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="btn-primary px-4 py-2">
                  {isSubmitting ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : (editingField ? 'Update' : 'Create')}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default StudentFieldsManager;

// import React, { useState, useEffect } from 'react';
// import { adminAPI } from '../../services/api';
// import toast from 'react-hot-toast';
// import Modal from '../../components/common/Modal';
// import { Formik, Form, Field } from 'formik';
// import * as Yup from 'yup';

// const StudentFieldsManager = () => {
//   const [fields, setFields] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showModal, setShowModal] = useState(false);
//   const [editingField, setEditingField] = useState(null);

//   useEffect(() => {
//     fetchFields();
//   }, []);

//   const fetchFields = async () => {
//     try {
//       setLoading(true);
//       const res = await adminAPI.getStudentFields();
//       setFields(res.data);
//     } catch (error) {
//       toast.error('Failed to load fields');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fieldSchema = Yup.object().shape({
//     field_name: Yup.string().required('Field name required'),
//     field_code: Yup.string()
//       .required('Field code required')
//       .matches(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, underscore'),
//     display_name: Yup.string().required('Display name required'),
//     field_type: Yup.string().required('Field type required'),
//     is_compulsory: Yup.boolean(),
//     is_unique: Yup.boolean(),
//     options: Yup.string().test('options-json', 'Invalid JSON for options', function(value) {
//       if (this.parent.field_type !== 'select') return true;
//       if (!value) return true;
//       try {
//         JSON.parse(value);
//         return true;
//       } catch {
//         return false;
//       }
//     }),
//     validation_rules: Yup.string().test('valid-json', 'Invalid JSON', function(value) {
//       if (!value) return true;
//       try {
//         JSON.parse(value);
//         return true;
//       } catch {
//         return false;
//       }
//     }),
//     display_order: Yup.number().integer().min(0).default(0),
//     is_active: Yup.boolean(),
//   });

//   const handleSave = async (values, { setSubmitting, resetForm }) => {
//     try {
//       const payload = {
//         ...values,
//         options: values.field_type === 'select' && values.options ? JSON.parse(values.options) : null,
//         validation_rules: values.validation_rules ? JSON.parse(values.validation_rules) : null,
//       };
//       if (editingField) {
//         await adminAPI.updateStudentField(editingField.id, payload);
//         toast.success('Field updated');
//       } else {
//         await adminAPI.createStudentField(payload);
//         toast.success('Field created');
//       }
//       setShowModal(false);
//       resetForm();
//       setEditingField(null);
//       fetchFields();
//     } catch (error) {
//       toast.error(error.response?.data?.detail || 'Operation failed');
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm('Delete this field? This may affect existing student data.')) return;
//     try {
//       await adminAPI.deleteStudentField(id);
//       toast.success('Field deleted');
//       fetchFields();
//     } catch (error) {
//       toast.error('Delete failed');
//     }
//   };

//   const openCreateModal = () => {
//     setEditingField(null);
//     setShowModal(true);
//   };

//   const openEditModal = (field) => {
//     setEditingField(field);
//     setShowModal(true);
//   };

//   if (loading) {
//     return <div className="flex justify-center items-center h-64"><div className="loading-spinner"></div></div>;
//   }

//   return (
//     <div className="space-y-6">
//       {/* Header */}
//       <div className="flex justify-between items-center">
//         <div>
//           <h1 className="text-2xl font-bold text-gray-800">Student Registration Fields</h1>
//           <p className="text-gray-600">Manage custom fields for student admission and profile</p>
//         </div>
//         <button onClick={openCreateModal} className="btn-primary flex items-center">
//           <i className="fas fa-plus mr-2"></i> Add Field
//         </button>
//       </div>

//       {/* Stats */}
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//         <div className="bg-white rounded-lg shadow p-4">
//           <div className="flex justify-between">
//             <div>
//               <p className="text-gray-500 text-sm">Total Fields</p>
//               <p className="text-2xl font-bold">{fields.length}</p>
//             </div>
//             <div className="p-3 bg-primary-100 rounded-full"><i className="fas fa-list text-primary-600"></i></div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4">
//           <div className="flex justify-between">
//             <div>
//               <p className="text-gray-500 text-sm">Active Fields</p>
//               <p className="text-2xl font-bold">{fields.filter(f => f.is_active).length}</p>
//             </div>
//             <div className="p-3 bg-green-100 rounded-full"><i className="fas fa-check-circle text-green-600"></i></div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4">
//           <div className="flex justify-between">
//             <div>
//               <p className="text-gray-500 text-sm">Compulsory Fields</p>
//               <p className="text-2xl font-bold">{fields.filter(f => f.is_compulsory).length}</p>
//             </div>
//             <div className="p-3 bg-yellow-100 rounded-full"><i className="fas fa-exclamation-triangle text-yellow-600"></i></div>
//           </div>
//         </div>
//       </div>

//       {/* Fields Table */}
//       <div className="bg-white rounded-lg shadow overflow-hidden">
//         <div className="p-6 border-b">
//           <h3 className="text-lg font-semibold">Custom Fields List</h3>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="min-w-full divide-y divide-gray-200">
//             <thead className="bg-gray-50">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Display Name</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field Code</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Options</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-gray-200">
//               {fields.map(field => (
//                 <tr key={field.id} className="hover:bg-gray-50">
//                   <td className="px-6 py-4">
//                     <div className="font-medium">{field.display_name}</div>
//                     <div className="text-xs text-gray-500">{field.field_name}</div>
//                   </td>
//                   <td className="px-6 py-4 font-mono text-sm">{field.field_code}</td>
//                   <td className="px-6 py-4">
//                     <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{field.field_type}</span>
//                   </td>
//                   <td className="px-6 py-4 text-sm">
//                     {field.field_type === 'select' && field.options ? field.options.join(', ') : '-'}
//                   </td>
//                   <td className="px-6 py-4">
//                     {field.is_compulsory ? <span className="text-red-600">Yes</span> : 'No'}
//                   </td>
//                   <td className="px-6 py-4">
//                     <span className={`px-2 py-1 text-xs rounded-full ${field.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
//                       {field.is_active ? 'Active' : 'Inactive'}
//                     </span>
//                   </td>
//                   <td className="px-6 py-4 space-x-2">
//                     <button onClick={() => openEditModal(field)} className="text-primary-600 hover:text-primary-900"><i className="fas fa-edit"></i></button>
//                     <button onClick={() => handleDelete(field.id)} className="text-red-600 hover:text-red-900"><i className="fas fa-trash"></i></button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* Add/Edit Modal */}
//       <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingField ? 'Edit Field' : 'Create New Field'} size="lg">
//         <Formik
//           initialValues={{
//             field_name: editingField?.field_name || '',
//             field_code: editingField?.field_code || '',
//             display_name: editingField?.display_name || '',
//             field_type: editingField?.field_type || 'text',
//             is_compulsory: editingField?.is_compulsory || false,
//             is_unique: editingField?.is_unique || false,
//             options: editingField?.options ? JSON.stringify(editingField.options) : '',
//             validation_rules: editingField?.validation_rules ? JSON.stringify(editingField.validation_rules) : '',
//             display_order: editingField?.display_order || 0,
//             is_active: editingField?.is_active ?? true,
//           }}
//           validationSchema={fieldSchema}
//           onSubmit={handleSave}
//         >
//           {({ isSubmitting, values, setFieldValue, errors, touched }) => (
//             <Form className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700">Field Name *</label>
//                   <Field name="field_name" className="input-field" placeholder="e.g., passport_url" />
//                   {errors.field_name && touched.field_name && <div className="text-red-500 text-sm">{errors.field_name}</div>}
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700">Field Code *</label>
//                   <Field name="field_code" className="input-field font-mono" placeholder="e.g., passport_url" />
//                   {errors.field_code && touched.field_code && <div className="text-red-500 text-sm">{errors.field_code}</div>}
//                 </div>
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700">Display Name *</label>
//                 <Field name="display_name" className="input-field" placeholder="e.g., Passport Photo URL" />
//                 {errors.display_name && touched.display_name && <div className="text-red-500 text-sm">{errors.display_name}</div>}
//               </div>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700">Field Type *</label>
//                   <Field as="select" name="field_type" className="input-field">
//                     <option value="text">Text</option>
//                     <option value="number">Number</option>
//                     <option value="date">Date</option>
//                     <option value="select">Select</option>
//                     <option value="boolean">Boolean (Yes/No)</option>
//                     <option value="file">File Upload</option>
//                   </Field>
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700">Display Order</label>
//                   <Field type="number" name="display_order" className="input-field" />
//                 </div>
//               </div>
//               {values.field_type === 'select' && (
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700">Options (JSON array)</label>
//                   <Field name="options" as="textarea" rows="2" className="input-field" placeholder='["Option 1", "Option 2"]' />
//                   <p className="text-xs text-gray-500">Enter a valid JSON array of strings</p>
//                 </div>
//               )}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700">Validation Rules (JSON, optional)</label>
//                 <Field name="validation_rules" as="textarea" rows="2" className="input-field" placeholder='{"min": 0, "max": 100}' />
//               </div>
//               <div className="flex space-x-6">
//                 <label className="flex items-center">
//                   <Field type="checkbox" name="is_compulsory" className="mr-2" />
//                   <span className="text-sm">Compulsory field</span>
//                 </label>
//                 <label className="flex items-center">
//                   <Field type="checkbox" name="is_unique" className="mr-2" />
//                   <span className="text-sm">Unique value (across students)</span>
//                 </label>
//                 <label className="flex items-center">
//                   <Field type="checkbox" name="is_active" className="mr-2" />
//                   <span className="text-sm">Active</span>
//                 </label>
//               </div>
//               <div className="flex justify-end space-x-3 pt-4">
//                 <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
//                 <button type="submit" disabled={isSubmitting} className="btn-primary px-4 py-2">
//                   {isSubmitting ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : (editingField ? 'Update' : 'Create')}
//                 </button>
//               </div>
//             </Form>
//           )}
//         </Formik>
//       </Modal>
//     </div>
//   );
// };

// export default StudentFieldsManager;